<template>
  <div class="lobe-interference-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>A(θ) = Σ e^(j·k·d·sinθ) | 相位阵列$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Radiation Pattern (Polar-like visualization) -->
          <g transform="translate(200, 200)">
             <!-- Grid lines -->
             <circle r="40" fill="none" stroke="rgba(255,255,255,0.05)" />
             <circle r="80" fill="none" stroke="rgba(255,255,255,0.05)" />
             <circle r="120" fill="none" stroke="rgba(255,255,255,0.05)" />
             
             <!-- The Lobe Path -->
             <path :d="patternPath" fill="none" stroke="#ffd700" stroke-width="2" opacity="0.8" />
             <path :d="patternPath" fill="rgba(255, 215, 0, 0.1)" />

             <!-- Main Beam direction indicator -->
             <line x1="0" y1="0" :x2="beamX" :y2="beamY" stroke="#00d2ff" stroke-width="1" stroke-dasharray="4,2" />
          </g>

          <!-- Array elements at the bottom -->
          <g transform="translate(200, 200)">
             <rect v-for="n in Number(elementCount)" :key="n" 
                   :x="(n - (Number(elementCount)+1)/2) * spacing" y="-5" 
                   width="4" height="10" fill="#22a6b3" />
          </g>

          <!-- Labels -->
          <text x="210" y="60" fill="#ffd700" font-size="10">主瓣</text>
          <text x="50" y="150" fill="#888" font-size="8">旁瓣</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>扫描角: {{ steerAngle }}°</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>扫描方向</label>
             <el-slider v-model="steerAngle" :min="-60" :max="60" size="mini"></el-slider>
          </div>
          <div class="row items-center mt-2">
             <label>阵元数量</label>
             <el-slider v-model="elementCount" :min="2" :max="16" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>波束成形（Beamforming）利用波的**干涉原理**。当一排波源（如天线阵列或音箱墙）同时发声，通过精密调节每个波源的相位延迟，可以在特定方向上产生相长干涉（主瓣），而在其他方向发生相消干涉。旁瓣（Sidelobes）则是干涉不完全导致的能量外泄。这是现代 5G 基站、相控阵雷达以及医疗 B 超的核心成像技术。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LobeInterferenceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      steerAngle: 0,
      elementCount: 8,
      spacing: 10
    }
  },
  computed: {
    patternPath() {
       let points = [];
       let N = Number(this.elementCount);
       let d = this.spacing / 10; // Normalized spacing
       let k = 2 * Math.PI; // Wave number
       let phi = k * d * Math.sin(this.steerAngle * Math.PI / 180); // Phase shift

       for (let deg = -180; deg <= 0; deg += 1) {
          let rad = deg * Math.PI / 180;
          let theta = rad + Math.PI/2;
          
          // Normalized Array Factor: AF = sin(N*psi/2) / (N*sin(psi/2))
          let psi = k * d * Math.sin(theta) - phi;
          let val;
          if (Math.abs(psi) < 0.001) {
             val = 1;
          } else {
             val = Math.abs(Math.sin(N * psi / 2) / (N * Math.sin(psi / 2)));
          }
          
          let r = val * 160;
          points.push(`${r * Math.cos(rad)},${r * Math.sin(rad)}`);
       }
       return "M 0,0 L " + points.join(" L ") + " Z";
    },
    beamX() {
       let rad = (this.steerAngle - 90) * Math.PI / 180;
       return 180 * Math.cos(rad);
    },
    beamY() {
       let rad = (this.steerAngle - 90) * Math.PI / 180;
       return 180 * Math.sin(rad);
    }
  }
}
</script>

<style scoped lang="scss">
.lobe-interference-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 215, 0, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
